import { InputNumberWithButtons } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { useToast, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useState } from "react";
import { Button } from "@jugl-web/ui-kit";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

export type MessageLimitFormState = {
  limit: number | null;
  periodMinutes: number | null;
  msg: string;
};

type MessageLimitSectionProps = {
  value: MessageLimitFormState;
  onChange: (patch: Partial<MessageLimitFormState>) => void;
  onSave: () => void;
  isSaving?: boolean;
  isDirty?: boolean;
};

const LIMIT_MIN = 1;
const LIMIT_MAX = 1000;
const PERIOD_MIN = 1;
const PERIOD_MAX = 60;

export const MessageLimitSection: FC<MessageLimitSectionProps> = ({
  value,
  onChange,
  onSave,
  isSaving = false,
  isDirty = false,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const [isMsgInvalid, setIsMsgInvalid] = useState(false);

  const showOutOfRangeToast = useCallback(
    (min: number, max: number) =>
      toast(
        t(
          {
            id: "cx-ai-agents-page.message-limit-value-range",
            defaultMessage: "Value can be from {min} to {max}",
          },
          { min, max }
        ),
        { variant: "error" }
      ),
    [t, toast]
  );

  const handleSave = () => {
    if (!value.msg.trim()) {
      setIsMsgInvalid(true);
      return;
    }

    setIsMsgInvalid(false);
    onSave();
  };

  return (
    <div className="jugl__custom-scrollbar-horizontal h-fit w-full shrink-0 overflow-x-auto rounded-3xl border border-solid border-dark-200 bg-white">
      <div className="flex w-full min-w-min flex-col gap-10 p-8">
        <div className="flex flex-col gap-2">
          <span className="font-secondary text-xl leading-[30px] font-semibold text-dark">
            🚫{" "}
            {t({
              id: "cx-ai-agents-page.message-limit-title",
              defaultMessage: "Message Limit",
            })}
          </span>
          <span className="font-secondary text-sm leading-[21px] text-dark-700">
            {t(
              {
                id: "cx-ai-agents-page.message-limit-description",
                defaultMessage:
                  "Limit the number of messages a single device can send via Conversations.{br}This limit helps to prevent spam messages and abuse.",
              },
              { br: <br /> }
            )}
          </span>
        </div>

        <div className="flex flex-col gap-7">
          <div className="flex items-center gap-2 font-secondary text-sm leading-[21px] whitespace-nowrap text-black">
            <span>
              {t({
                id: "cx-ai-agents-page.message-limit-to",
                defaultMessage: "Limit to",
              })}
            </span>
            <InputNumberWithButtons
              value={value.limit}
              onChange={(limit) => onChange({ limit })}
              min={LIMIT_MIN}
              max={LIMIT_MAX}
              onOutOfRange={({ min, max }) => showOutOfRangeToast(min, max)}
            />
            <span>
              {t({
                id: "cx-ai-agents-page.message-limit-messages-in-a-row",
                defaultMessage: "messages in a row, for each",
              })}
            </span>
            <InputNumberWithButtons
              value={value.periodMinutes}
              onChange={(periodMinutes) => onChange({ periodMinutes })}
              min={PERIOD_MIN}
              max={PERIOD_MAX}
              onOutOfRange={({ min, max }) => showOutOfRangeToast(min, max)}
            />
            <span>
              {t({
                id: "cx-ai-agents-page.message-limit-minutes",
                defaultMessage: "minutes",
              })}
            </span>
          </div>

          <TextAreaInput
            label={t({
              id: "cx-ai-agents-page.message-limit-reached-label",
              defaultMessage: "When limit reached, show this message",
            })}
            labelPosition="border-overlap"
            value={value.msg}
            isInvalid={isMsgInvalid}
            errorMessage={
              isMsgInvalid
                ? t({
                    id: "feedback.mandatory-field",
                    defaultMessage: "Mandatory Field",
                  })
                : undefined
            }
            onChange={(e) => {
              const msg = e.target.value.trimStart();
              onChange({ msg });
              if (isMsgInvalid && msg.trim()) {
                setIsMsgInvalid(false);
              }
            }}
          />
        </div>

        <Button
          variant="secondary"
          onClick={handleSave}
          isDisabled={
            isSaving ||
            !isDirty ||
            value.limit === null ||
            value.periodMinutes === null
          }
          iconEnd={<ArrowIcon />}
          className="w-[140px]"
        >
          {t({
            id: "common.save",
            defaultMessage: "Save",
          })}
        </Button>
      </div>
    </div>
  );
};
